'use client';
import React from 'react';
import Link from 'next/link';

const YEMEN_EMBLEM = 'https://media.base44.com/images/public/69ed0fe31d3c9ede3b821281/406079c5f_Emblem_of_Yemen_2svg.png';

export default function HeroSection() {
  return (
    <section className="relative overflow-hidden" dir="rtl">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/95 to-accent/80"></div>
      {/* Pattern overlay */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />
      {/* Decorative circles */}
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white/5 blur-3xl"></div>
      <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-accent/20 blur-3xl"></div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-28">
        <div className="text-center">
          {/* Emblem + Title */}
          <div className="mb-6 animate-fade-in">
            <div className="inline-flex flex-col items-center gap-3">
              <img
                src={YEMEN_EMBLEM}
                alt="شعار اليمن"
                className="w-20 h-20 object-contain drop-shadow-xl"
              />
              <div className="text-center">
                <div className="text-white font-extrabold text-3xl tracking-widest leading-none font-tajawal">
                  منصة تصريح
                </div>
                <div className="text-white/70 text-base mt-1 tracking-wide font-medium">
                  وزارة الخارجية وشؤون المغتربين — الجمهورية اليمنية
                </div>
              </div>
            </div>
          </div>

          {/* Official Badge */}
          <div className="mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 text-white border border-white/20 backdrop-blur-sm text-xs font-medium">
              🇧🇴 وزارة الخارجية — الجمهورية اليمنية
            </span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight tracking-tight">
            بوابة السفر والتأشيرات
            <br />
            <span className="text-white/80">الرسمية اليمنية</span>
          </h1>

          {/* Subtitle */}
          <p className="mt-6 text-base sm:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
            للمواطنين اليمنيين: احصل على الموافقة الأمنية للسفر إلى مصر والأردن.
            للزوار والسياح الأجانب: تقديم طلب تأشيرة دخول اليمن — كل ذلك في منصة واحدة آمنة.
          </p>

          {/* CTA Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 flex-wrap">
            <Link href="/apply?mode=yemeni_clearance">
              <button className="inline-flex items-center justify-center gap-2 bg-white text-primary hover:bg-white/90 font-semibold px-7 text-sm h-12 rounded-xl shadow-lg shadow-black/10 transition-all duration-200 hover:scale-105">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                موافقة أمنية — للمواطنين
              </button>
            </Link>
            <Link href="/apply?mode=yemen_visa">
              <button className="inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent-dark text-white font-semibold px-7 text-sm h-12 rounded-xl shadow-lg shadow-black/10 transition-all duration-200 hover:scale-105">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                تأشيرة اليمن — للزوار
              </button>
            </Link>
            <Link href="/track">
              <button className="inline-flex items-center justify-center gap-2 border border-white/30 text-white hover:bg-white/10 font-medium px-7 text-sm h-12 rounded-xl transition-all duration-200">
                تتبع طلبي
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            </Link>
          </div>

          {/* Trust Badges */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {[
              'بوابة حكومية رسمية',
              'آمنة ومشفرة',
              'تحديثات فورية',
              'دعم عربي/إنجليزي',
            ]?.map((badge) => (
              <div key={badge} className="flex items-center gap-1.5 text-xs text-white/60">
                <svg className="w-3.5 h-3.5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {badge}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
